import { useState } from 'react';
import {
	ContextMenuItem,
	CustomizationData,
	Events,
	storageExtensionSlots,
	StorageFileTableParams,
} from '@sharefiledev/storage-plugin-pilet-sdk';
import { theme } from 'antd';
import { useAppContext } from '../../../../AppContext';
import { pendoSendTrackEvent, trackUploadFileSuccess } from '../../../../tracking';
import { getLanguage, t } from '../../../../utils';
import { usePluginEventListener } from '../../../../utils/hooks/events/pluginEvents';

interface Props {
	containerRID: string;
	uploadBtn?: {
		size?: 'large' | 'middle' | 'small';
		type?: 'default' | 'primary' | 'link' | 'text' | 'dashed';
		label?: string;
	};
	fileUploadComplete?: () => void;
	fileDelete?: () => void;
	disabled?: boolean;
}

export const FileBrowserTable = (props: Props) => {
	const { useToken } = theme;
	const { token } = useToken();
	const userLanguage = getLanguage();
	const { piletApi } = useAppContext();
	const [selectedFiles, setSelectedFiles] = useState<number>();

	const Extension = piletApi.Extension as React.FC<any>;

	//pendo event for file successful upload
	const fileUploadSuccessCallback = (count: number) => {
		pendoSendTrackEvent(trackUploadFileSuccess);
		if (count === selectedFiles) {
			props.fileUploadComplete && props.fileUploadComplete();
		}
	};

	const fileDeleteSuccessCallback = () => {
		props.fileDelete && props.fileDelete();
	};

	usePluginEventListener(Events.DeleteFileChannel, fileDeleteSuccessCallback);
	usePluginEventListener(
		`${Events.CountUpdated}-${props.containerRID}`,
		({ count }: { count?: number }) => {
			if (count) {
				fileUploadSuccessCallback(count);
			}
		}
	);
	usePluginEventListener(Events.UploadFileStartChannel, (event: string[]) => {
		setSelectedFiles(event.length);
	});

	const configureContextMenu = () => {
		const uploadMenuItem: ContextMenuItem = {
			menuType: 'upload',
			type: props.uploadBtn?.type ?? 'default',
			size: props.uploadBtn?.size ?? 'middle',
		};

		const downloadMenuItem: ContextMenuItem = {
			menuType: 'download',
			type: 'default',
		};

		const deleteMenuItem: ContextMenuItem = {
			menuType: 'delete',
			type: 'default',
		};
		if (props.disabled) {
			return [downloadMenuItem];
		}
		return [uploadMenuItem, downloadMenuItem, deleteMenuItem];
	};

	const customization: CustomizationData = {
		columnsList: [],
		contextMenu: configureContextMenu(),
		disableColumnSorting: true,
		disableSelection: true,
		disableTableHeader: true,
		pagination: {
			onlyDisplayFirstPage: true,
			allowedPageSizes: [10, 20, 25, 50, 75, 100],
			defaultPageSize: 100,
			// this is a temporary fix to disable infinite scroll
			// until the plugin supports it properly
			// this will be removed in the future
			enableInfiniteScroll: false,
		},
		labels: [
			{
				key: 'upload.upload',
				value: {
					[userLanguage]:
						props.uploadBtn?.label ??
						t('dynamic-components:fileBrowserPlugin.uploadBtnDefault'),
				},
			},
			{
				key: 'upload.empty.title',
				value: { [userLanguage]: t('dynamic-components:fileBrowserPlugin.empty.title') },
			},
			{
				key: 'upload.empty.message',
				value: { [userLanguage]: ' ' },
			},
		],
		styles: {
			fileViewerZIndex: token.zIndexPopupBase,
		},
	};

	const filesTableParams: StorageFileTableParams = {
		containerRID: props.containerRID,
		customizationData: customization,
	};

	return <Extension name={storageExtensionSlots.FilesTable} params={filesTableParams} />;
};
